import { openDB, IDBPDatabase } from 'idb';
import { 
  INDEXEDDB_VERSION, 
  INDEXEDDB_NAME, 
  CACHE_MAX_AGE_HOURS,
  CACHE_INSTANT_SHOW_THRESHOLD_MINUTES 
} from '@/config/cache.config';
import { logDebug, logWarn } from '@/lib/logger';
import { devLog } from '@/lib/utils';
import { 
  addAccessTimestamp, 
  checkAndEvictIfNeeded,
} from '@/lib/cache-lru';

/**
 * Registro genérico almacenado en IndexedDB.
 * Todos los stores comparten esta forma base; los campos extra son `unknown`
 * porque el schema evoluciona rápido y no vale la pena tipar cada store.
 */
/**
 * Registro guardado en el cache offline (IndexedDB).
 *
 * OJO con `[key: string]: unknown`: esa firma de indice hace que CUALQUIER campo que no este
 * declarado arriba salga como `unknown`. Por eso las pantallas que leian del cache (POS, Menu,
 * KDS, Mesas) tenian los campos sin tipo y arrastraban ~16 errores: `producto.name` era
 * `unknown`, `.localeCompare` no existia, etc.
 *
 * Las funciones de cache de abajo son GENERICAS: cada pantalla declara QUE guarda
 * (`getCachedProductsIfFresh<Product>()`) y recupera sus tipos, sin castear.
 */
export interface CachedRecord {
  id: string;
  tenant_id?: string;
  branch_id?: string;
  _last_accessed?: number;
  _cached_at?: number;
  [key: string]: unknown;
}

// Nota: en este repo el schema evoluciona rápido. Para evitar fricción con `idb`,
// usamos `any` en el tipo del DB (los stores están definidos en `upgrade`).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let dbInstance: IDBPDatabase<any> | null = null;
/** Evita dos `openDB` en paralelo (React Strict Mode / varias rutas llamando a la vez). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let dbInitPromise: Promise<IDBPDatabase<any>> | null = null;

// Inicializar la base de datos
export async function initDB() {
  if (dbInstance) return dbInstance;
  if (dbInitPromise) return dbInitPromise;

  // Nota: `idb` es estricto con `DBSchema` y este repo evoluciona rápido.
  // Si el schema tipado se desincroniza, preferimos priorizar estabilidad del build.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dbInitPromise = openDB<any>(INDEXEDDB_NAME, INDEXEDDB_VERSION, {
    upgrade(db, oldVersion, newVersion, transaction) {
      logDebug(`Upgrade DB v${oldVersion} → v${newVersion}`);

      // Limpiar datos antiguos si upgrade desde v10 o anterior
      if (oldVersion > 0 && oldVersion < 11) {
        const storesToClear = ['products', 'modifiers', 'orders', 'orderItems', 'categories', 'tables'];
        storesToClear.forEach(storeName => {
          if (db.objectStoreNames.contains(storeName)) {
            transaction.objectStore(storeName).clear();
          }
        });
      }

      // Crear stores si no existen
      if (!db.objectStoreNames.contains('products')) {
        const productStore = db.createObjectStore('products', { keyPath: 'id' });
        productStore.createIndex('by-category', 'category');
        productStore.createIndex('by-active', 'is_active');
      }

      if (!db.objectStoreNames.contains('pendingOrders')) {
        const orderStore = db.createObjectStore('pendingOrders', { keyPath: 'id' });
        orderStore.createIndex('by-created', 'created_at');
      }

      if (!db.objectStoreNames.contains('categories')) {
        db.createObjectStore('categories', { keyPath: 'id' });
      }

      if (!db.objectStoreNames.contains('modifiers')) {
        const modifierStore = db.createObjectStore('modifiers', { keyPath: 'id' });
        modifierStore.createIndex('by-product', 'product_id');
      }

      if (!db.objectStoreNames.contains('orders')) {
        const ordersStore = db.createObjectStore('orders', { keyPath: 'id' });
        ordersStore.createIndex('by-status', 'status');
        ordersStore.createIndex('by-date', 'created_at');
      }

      if (!db.objectStoreNames.contains('orderItems')) {
        const itemsStore = db.createObjectStore('orderItems', { keyPath: 'id' });
        itemsStore.createIndex('by-order', 'order_id');
      }

      if (!db.objectStoreNames.contains('syncMetadata')) {
        db.createObjectStore('syncMetadata', { keyPath: 'key' });
      }

      if (!db.objectStoreNames.contains('tables')) {
        const tablesStore = db.createObjectStore('tables', { keyPath: 'id' });
        tablesStore.createIndex('by-branch', 'branch_id');
        tablesStore.createIndex('by-status', 'status');
      }
    },
  }).then((db) => {
    dbInstance = db;
    logDebug('IndexedDB inicializada');
    return db;
  });

  try {
    return await dbInitPromise;
  } finally {
    dbInitPromise = null;
  }
}

// ==================== PRODUCTOS ====================

/**
 * Cachear productos con validación y limpieza automática
 * 
 * Estrategia:
 * 1. Limpia TODOS los productos viejos (evita productos obsoletos)
 * 2. Guarda solo los productos nuevos/frescos
 * 3. Actualiza metadata de sincronización
 * 
 * @param products Array de productos a cachear (debe incluir tenant_id e is_active)
 */
export async function cacheProducts<T extends CachedRecord>(products: T[]) {
  const db = await initDB();
  const cachedTenantId = localStorage.getItem('cached_tenant_id');
  
  try {
    // 🔧 VALIDACIÓN: Filtrar productos sin tenant_id o con tenant_id incorrecto
    const validProducts = products.filter(p => {
      if (!p.tenant_id && cachedTenantId) {
        // Si no tiene tenant_id pero hay uno en caché, asignarlo
        p.tenant_id = cachedTenantId;
      }
      return p.tenant_id === cachedTenantId;
    });

    if (validProducts.length !== products.length) {
      logWarn(`[cacheProducts] ${products.length - validProducts.length} productos filtrados (tenant_id incorrecto)`);
    }

    // 🔧 FIX: Ordenar productos alfabéticamente antes de guardar (consistencia con Supabase)
    const sortedProducts = [...validProducts].sort((a, b) =>
      String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, { numeric: true, sensitivity: 'base' })
    );
    
    // 🚨 LRU: Verificar límites antes de agregar
    await checkAndEvictIfNeeded(db, 'products', sortedProducts.length, cachedTenantId);
    
    // 🚨 LIMPIEZA COMPLETA: Eliminar TODOS los productos viejos antes de guardar nuevos
    // Esto evita que queden productos obsoletos en el caché
    await db.clear('products');
    
    // Guardar productos frescos con timestamp de acceso
    const tx = db.transaction('products', 'readwrite');
    await Promise.all(
      sortedProducts.map(product => {
        const withTimestamp = addAccessTimestamp(product);
        return tx.store.put(withTimestamp);
      })
    );
    await tx.done;
    
    logDebug(`[cacheProducts] ${sortedProducts.length} productos cacheados (caché limpiado y actualizado)`);

    // Actualizar metadata de sincronización
    await setSyncMetadata('products', new Date().toISOString(), 'success');
  } catch (error) {
    devLog.error('Error al guardar productos', error);
    throw error;
  }
}

/**
 * Obtener productos del caché con validación de frescura
 * 
 * Estrategia:
 * 1. Verifica que el caché sea fresco (< threshold minutos)
 * 2. Filtra por tenant_id (seguridad multi-tenant)
 * 3. Valida integridad de datos (tenant_id presente)
 * 
 * Con threshold = 0: Siempre devuelve [] para forzar loading
 * Con threshold > 0: Devuelve productos si son frescos
 */
export async function getCachedProductsIfFresh<T = CachedRecord>(): Promise<T[]> {
  const cacheEnabled = localStorage.getItem('cache_enabled');
  if (cacheEnabled === 'false') return [];

  const db = await initDB();
  const cachedTenantId = localStorage.getItem('cached_tenant_id');
  
  if (!cachedTenantId) {
    logDebug('[getCachedProductsIfFresh] No hay tenant_id en caché');
    return [];
  }
  
  const metadata = await db.get('syncMetadata', 'products');
  if (!metadata) {
    logDebug('[getCachedProductsIfFresh] No hay metadata de sincronización');
    return [];
  }

  const minutesSinceSync = (Date.now() - new Date(metadata.last_sync).getTime()) / 60000;
  
  // Validar frescura del caché
  if (minutesSinceSync > CACHE_INSTANT_SHOW_THRESHOLD_MINUTES) {
    logDebug(`[getCachedProductsIfFresh] Caché no es fresco (${minutesSinceSync.toFixed(1)}min > ${CACHE_INSTANT_SHOW_THRESHOLD_MINUTES}min)`);
    return [];
  }
  
  const allProducts = await db.getAll('products');
  
  // 🔧 VALIDACIÓN: Filtrar productos sin tenant_id o con tenant_id incorrecto
  // Estos son productos viejos/obsoletos que deben eliminarse
  const validProducts = allProducts.filter(p => {
    if (!p.tenant_id) {
      // Producto viejo sin tenant_id, ignorarlo
      return false;
    }
    return p.tenant_id === cachedTenantId;
  });
  
  // 🚨 LIMPIEZA: Si hay productos inválidos, limpiarlos del caché
  const invalidProducts = allProducts.filter(p => !p.tenant_id || p.tenant_id !== cachedTenantId);
  if (invalidProducts.length > 0) {
    logDebug(`[getCachedProductsIfFresh] Limpiando ${invalidProducts.length} productos inválidos del caché`);
    const tx = db.transaction('products', 'readwrite');
    await Promise.all(
      invalidProducts.map(p => tx.store.delete(p.id))
    );
    await tx.done;
  }
  
  // 🔧 FIX: Ordenar productos alfabéticamente (consistencia con Supabase)
  const sorted = validProducts.sort((a, b) => 
    (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' })
  );
  
  logDebug(`[getCachedProductsIfFresh] ${sorted.length} productos válidos desde caché (fresco: ${minutesSinceSync.toFixed(1)}min)`);
  return sorted;
}

/**
 * Obtener productos para fallback en errores (sin validación estricta de frescura)
 * 
 * Usado cuando hay errores de red y necesitamos mostrar algo al usuario
 * Aún así valida tenant_id y limpia productos inválidos
 */
export async function getCachedProducts<T = CachedRecord>(): Promise<T[]> {
  const cacheEnabled = localStorage.getItem('cache_enabled');
  if (cacheEnabled === 'false') {
    logDebug('[getCachedProducts] Cache deshabilitado por usuario');
    return [];
  }

  const db = await initDB();
  const cachedTenantId = localStorage.getItem('cached_tenant_id');
  if (!cachedTenantId) {
    logDebug('[getCachedProducts] No hay tenant_id en localStorage');
    return [];
  }
  
  const metadata = await db.get('syncMetadata', 'products');
  if (metadata) {
    const hoursSinceSync = (Date.now() - new Date(metadata.last_sync).getTime()) / 3600000;
    if (hoursSinceSync > CACHE_MAX_AGE_HOURS) {
      logDebug(`[getCachedProducts] Cache expirado (${hoursSinceSync.toFixed(2)}h > ${CACHE_MAX_AGE_HOURS}h)`);
      return [];
    }
  }
  
  const allProducts = await db.getAll('products');
  logDebug(`[getCachedProducts] ${allProducts.length} productos en IndexedDB total`);
  
  // 🔧 VALIDACIÓN: Filtrar productos sin tenant_id o con tenant_id incorrecto
  const validProducts = allProducts.filter(p => {
    if (!p.tenant_id) {
      // Producto viejo sin tenant_id, ignorarlo
      return false;
    }
    return p.tenant_id === cachedTenantId;
  });
  
  // 🚨 LIMPIEZA: Eliminar productos inválidos del caché
  const invalidProducts = allProducts.filter(p => !p.tenant_id || p.tenant_id !== cachedTenantId);
  if (invalidProducts.length > 0) {
    logDebug(`[getCachedProducts] Limpiando ${invalidProducts.length} productos inválidos del caché`);
    const tx = db.transaction('products', 'readwrite');
    await Promise.all(
      invalidProducts.map(p => tx.store.delete(p.id))
    );
    await tx.done;
  }
  
  logDebug(`[getCachedProducts] ${validProducts.length} productos válidos después de filtrar por tenant`);
  
  // 🔧 FIX: Ordenar productos alfabéticamente (consistencia con Supabase)
  const sorted = validProducts.sort((a, b) => 
    (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' })
  );
  
  return sorted;
}

async function _getCachedProduct(id: string): Promise<CachedRecord | undefined> {
  const cacheEnabled = localStorage.getItem('cache_enabled');
  if (cacheEnabled === 'false') return undefined;

  const db = await initDB();
  return await db.get('products', id);
}

async function _clearProductsCache() {
  const db = await initDB();
  await db.clear('products');
}

// ==================== MESAS (TABLES) ====================

/**
 * Cachear mesas con validación y limpieza automática
 * 
 * Estrategia:
 * 1. Limpia TODAS las mesas viejas (evita mesas obsoletas)
 * 2. Guarda solo las mesas nuevas/frescas
 * 3. Actualiza metadata de sincronización
 * 
 * @param tables Array de mesas a cachear (debe incluir branch_id)
 */
export async function cacheTables<T extends CachedRecord>(tables: T[]) {
  const db = await initDB();
  const cachedBranchId = localStorage.getItem('cached_branch_id');
  
  try {
    // 🔧 VALIDACIÓN: Filtrar mesas sin branch_id o con branch_id incorrecto
    const validTables = tables.filter(t => {
      if (!t.branch_id && cachedBranchId) {
        // Si no tiene branch_id pero hay uno en caché, asignarlo
        t.branch_id = cachedBranchId;
      }
      return t.branch_id === cachedBranchId;
    });

    if (validTables.length !== tables.length) {
      devLog.warn(`⚠️ [cacheTables] ${tables.length - validTables.length} mesas filtradas (branch_id incorrecto)`);
    }

    // 🔧 FIX: Ordenar mesas por número antes de guardar (consistencia con Supabase)
    const sortedTables = [...validTables].sort((a, b) =>
      String(a.table_number ?? '').localeCompare(String(b.table_number ?? ''), undefined, { numeric: true, sensitivity: 'base' })
    );
    
    // 🚨 LRU: Verificar límites antes de agregar
    await checkAndEvictIfNeeded(db, 'tables', sortedTables.length, cachedBranchId);
    
    // 🚨 LIMPIEZA COMPLETA: Eliminar TODAS las mesas viejas antes de guardar nuevas
    // Esto evita que queden mesas obsoletas en el caché
    await db.clear('tables');
    
    // Guardar mesas frescas con timestamp de acceso
    const tx = db.transaction('tables', 'readwrite');
    await Promise.all(
      sortedTables.map(table => {
        const withTimestamp = addAccessTimestamp(table);
        return tx.store.put(withTimestamp);
      })
    );
    await tx.done;
    
    logDebug(`[cacheTables] ${sortedTables.length} mesas cacheadas (caché limpiado y actualizado)`);

    // Actualizar metadata de sincronización
    await setSyncMetadata('tables', new Date().toISOString(), 'success');
  } catch (error) {
    devLog.error('❌ Error al guardar mesas:', error);
    throw error;
  }
}

/**
 * Obtener mesas del caché con validación de frescura
 * 
 * Estrategia:
 * 1. Verifica que el caché sea fresco (< threshold minutos)
 * 2. Filtra por branch_id (seguridad multi-branch)
 * 3. Valida integridad de datos (branch_id presente)
 * 
 * Con threshold = 0: Siempre devuelve [] para forzar loading
 * Con threshold > 0: Devuelve mesas si son frescas
 */
export async function getCachedTablesIfFresh<T = CachedRecord>(): Promise<T[]> {
  const cacheEnabled = localStorage.getItem('cache_enabled');
  if (cacheEnabled === 'false') return [];

  const db = await initDB();
  const cachedBranchId = localStorage.getItem('cached_branch_id');
  
  if (!cachedBranchId) {
    logDebug('[getCachedTablesIfFresh] No hay branch_id en caché');
    return [];
  }
  
  const metadata = await db.get('syncMetadata', 'tables');
  if (!metadata) {
    logDebug('[getCachedTablesIfFresh] No hay metadata de sincronización');
    return [];
  }

  const minutesSinceSync = (Date.now() - new Date(metadata.last_sync).getTime()) / 60000;
  
  // Validar frescura del caché
  if (minutesSinceSync > CACHE_INSTANT_SHOW_THRESHOLD_MINUTES) {
    logDebug(`[getCachedTablesIfFresh] Caché no es fresco (${minutesSinceSync.toFixed(1)}min > ${CACHE_INSTANT_SHOW_THRESHOLD_MINUTES}min)`);
    return [];
  }
  
  const allTables = await db.getAll('tables');
  
  // 🔧 VALIDACIÓN: Filtrar mesas sin branch_id o con branch_id incorrecto
  // Estas son mesas viejas/obsoletas que deben eliminarse
  const validTables = allTables.filter(t => {
    if (!t.branch_id) {
      // Mesa vieja sin branch_id, ignorarla
      return false;
    }
    return t.branch_id === cachedBranchId;
  });
  
  // 🚨 LIMPIEZA: Si hay mesas inválidas, limpiarlas del caché
  const invalidTables = allTables.filter(t => !t.branch_id || t.branch_id !== cachedBranchId);
  if (invalidTables.length > 0) {
    logDebug(`[getCachedTablesIfFresh] Limpiando ${invalidTables.length} mesas inválidas del caché`);
    const tx = db.transaction('tables', 'readwrite');
    await Promise.all(
      invalidTables.map(t => tx.store.delete(t.id))
    );
    await tx.done;
  }
  
  // 🔧 FIX: Ordenar mesas por número (consistencia con Supabase)
  const sorted = validTables.sort((a, b) => 
    (a.table_number || '').localeCompare(b.table_number || '', undefined, { numeric: true, sensitivity: 'base' })
  );
  
  logDebug(`[getCachedTablesIfFresh] ${sorted.length} mesas válidas desde caché (fresco: ${minutesSinceSync.toFixed(1)}min)`);
  return sorted;
}

/**
 * Obtener mesas para fallback en errores (sin validación estricta de frescura)
 * 
 * Usado cuando hay errores de red y necesitamos mostrar algo al usuario
 * Aún así valida branch_id y limpia mesas inválidas
 */
export async function getCachedTables<T = CachedRecord>(): Promise<T[]> {
  const cacheEnabled = localStorage.getItem('cache_enabled');
  if (cacheEnabled === 'false') {
    logDebug('[getCachedTables] Cache deshabilitado por usuario');
    return [];
  }

  const db = await initDB();
  const cachedBranchId = localStorage.getItem('cached_branch_id');
  if (!cachedBranchId) {
    logDebug('[getCachedTables] No hay branch_id en localStorage');
    return [];
  }
  
  const metadata = await db.get('syncMetadata', 'tables');
  if (metadata) {
    const hoursSinceSync = (Date.now() - new Date(metadata.last_sync).getTime()) / 3600000;
    if (hoursSinceSync > CACHE_MAX_AGE_HOURS) {
      logDebug(`[getCachedTables] Cache expirado (${hoursSinceSync.toFixed(2)}h > ${CACHE_MAX_AGE_HOURS}h)`);
      return [];
    }
  }
  
  const allTables = await db.getAll('tables');
  logDebug(`[getCachedTables] ${allTables.length} mesas en IndexedDB total`);
  
  // 🔧 VALIDACIÓN: Filtrar mesas sin branch_id o con branch_id incorrecto
  const validTables = allTables.filter(t => {
    if (!t.branch_id) {
      // Mesa vieja sin branch_id, ignorarla
      return false;
    }
    return t.branch_id === cachedBranchId;
  });
  
  // 🚨 LIMPIEZA: Eliminar mesas inválidas del caché
  const invalidTables = allTables.filter(t => !t.branch_id || t.branch_id !== cachedBranchId);
  if (invalidTables.length > 0) {
    logDebug(`[getCachedTables] Limpiando ${invalidTables.length} mesas inválidas del caché`);
    const tx = db.transaction('tables', 'readwrite');
    await Promise.all(
      invalidTables.map(t => tx.store.delete(t.id))
    );
    await tx.done;
  }
  
  logDebug(`[getCachedTables] ${validTables.length} mesas válidas después de filtrar por branch`);
  
  // 🔧 FIX: Ordenar mesas por número (consistencia con Supabase)
  const sorted = validTables.sort((a, b) => 
    (a.table_number || '').localeCompare(b.table_number || '', undefined, { numeric: true, sensitivity: 'base' })
  );
  
  return sorted;
}

async function _clearTablesCache() {
  const db = await initDB();
  await db.clear('tables');
}

// ==================== CATEGORÍAS ====================

async function _cacheCategories(categories: CachedRecord[]) {
  const db = await initDB();
  const tx = db.transaction('categories', 'readwrite');
  await Promise.all(categories.map(cat => tx.store.put(cat)));
  await tx.done;
}

async function _getCachedCategories(): Promise<CachedRecord[]> {
  const cacheEnabled = localStorage.getItem('cache_enabled');
  if (cacheEnabled === 'false') return [];

  const db = await initDB();
  return await db.getAll('categories');
}

// ==================== MODIFICADORES ====================

export async function cacheModifiers<T extends CachedRecord>(modifiers: T[]) {
  const db = await initDB();
  await db.clear('modifiers');
  const tx = db.transaction('modifiers', 'readwrite');
  await Promise.all(modifiers.map(mod => tx.store.put(mod)));
  await tx.done;
  logDebug(`${modifiers.length} modificadores cacheados`);
}

export async function getCachedModifiersByProduct<T = CachedRecord>(productId: string): Promise<T[]> {
  const cacheEnabled = localStorage.getItem('cache_enabled');
  if (cacheEnabled === 'false') return [];

  const db = await initDB();
  return await db.getAllFromIndex('modifiers', 'by-product', productId);
}

async function clearModifiersCache() {
  const db = await initDB();
  await db.clear('modifiers');
}

// ==================== ÓRDENES (KDS & Orders Page) ====================

export async function cacheOrders<T extends CachedRecord>(orders: T[]) {
  const db = await initDB();
  const cachedTenantId = localStorage.getItem('cached_tenant_id');
  
  // 🚨 LRU: Verificar límites antes de agregar
  await checkAndEvictIfNeeded(db, 'orders', orders.length, cachedTenantId);
  
  await db.clear('orders');
  const tx = db.transaction('orders', 'readwrite');
  await Promise.all(
    orders.map(order => {
      const withTimestamp = addAccessTimestamp(order);
      return tx.store.put(withTimestamp);
    })
  );
  await tx.done;
  logDebug(`${orders.length} órdenes cacheadas`);
  await setSyncMetadata('orders', new Date().toISOString(), 'success');
}

/**
 * Obtener órdenes del caché (con validación de frescura)
 */
async function _getCachedOrdersIfFresh(): Promise<CachedRecord[]> {
  const cacheEnabled = localStorage.getItem('cache_enabled');
  if (cacheEnabled === 'false') return [];

  const db = await initDB();
  const cachedTenantId = localStorage.getItem('cached_tenant_id');
  if (!cachedTenantId) return [];
  
  const metadata = await db.get('syncMetadata', 'orders');
  if (!metadata) return [];

  const minutesSinceSync = (Date.now() - new Date(metadata.last_sync).getTime()) / 60000;
  
  if (minutesSinceSync > CACHE_INSTANT_SHOW_THRESHOLD_MINUTES) {
    logDebug(`Caché de órdenes no es fresco (${minutesSinceSync.toFixed(1)}min)`);
    return [];
  }
  
  const allOrders = await db.getAllFromIndex('orders', 'by-date');
  const tenantOrders = allOrders.filter(o => o.tenant_id === cachedTenantId);
  
  logDebug(`📦 ${tenantOrders.length} órdenes desde caché`);
  return tenantOrders.reverse();
}

/**
 * Obtener órdenes para fallback en errores
 */
async function _getCachedOrders(): Promise<CachedRecord[]> {
  const cacheEnabled = localStorage.getItem('cache_enabled');
  if (cacheEnabled === 'false') return [];

  const db = await initDB();
  const cachedTenantId = localStorage.getItem('cached_tenant_id');
  if (!cachedTenantId) return [];
  
  const metadata = await db.get('syncMetadata', 'orders');
  if (metadata) {
    const hoursSinceSync = (Date.now() - new Date(metadata.last_sync).getTime()) / 3600000;
    if (hoursSinceSync > CACHE_MAX_AGE_HOURS) return [];
  }
  
  const allOrders = await db.getAllFromIndex('orders', 'by-date');
  return allOrders.filter(o => o.tenant_id === cachedTenantId).reverse();
}

export async function getCachedOrdersByStatus<T = CachedRecord>(status: string[]): Promise<T[]> {
  const cacheEnabled = localStorage.getItem('cache_enabled');
  if (cacheEnabled === 'false') return [];

  const db = await initDB();
  const cachedTenantId = localStorage.getItem('cached_tenant_id');
  if (!cachedTenantId) return [];
  
  const allOrders = await db.getAll('orders');
  const filtered = allOrders.filter(order => 
    order.tenant_id === cachedTenantId && status.includes(order.status)
  );
  
  return filtered.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
}

export async function cacheOrderItems<T extends CachedRecord>(items: T[]) {
  const db = await initDB();
  await db.clear('orderItems');
  const tx = db.transaction('orderItems', 'readwrite');
  await Promise.all(items.map(item => tx.store.put(item)));
  await tx.done;
}

export async function getCachedOrderItems<T = CachedRecord>(orderId: string): Promise<T[]> {
  const cacheEnabled = localStorage.getItem('cache_enabled');
  if (cacheEnabled === 'false') return [];

  const db = await initDB();
  return await db.getAllFromIndex('orderItems', 'by-order', orderId);
}

async function _clearOrdersCache() {
  const db = await initDB();
  await db.clear('orders');
  await db.clear('orderItems');
}

// ==================== ÓRDENES PENDIENTES (Sincronización) ====================

export async function addPendingOrder<T extends object>(orderData: T) {
  const db = await initDB();
  const id = `pending-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const pendingOrder = {
    id,
    order_data: orderData,
    created_at: new Date().toISOString(),
    retry_count: 0,
  };

  await db.put('pendingOrders', pendingOrder);
  logDebug('📝 Orden agregada a cola offline', { id });
  return id;
}

export async function getPendingOrders(): Promise<CachedRecord[]> {
  const db = await initDB();
  return await db.getAllFromIndex('pendingOrders', 'by-created');
}

export async function removePendingOrder(id: string) {
  const db = await initDB();
  await db.delete('pendingOrders', id);
  logDebug('✅ Orden sincronizada y eliminada de cola', { id });
}

export async function incrementRetryCount(id: string) {
  const db = await initDB();
  const order = await db.get('pendingOrders', id);
  if (order) {
    order.retry_count += 1;
    await db.put('pendingOrders', order);
  }
}

async function _clearPendingOrders() {
  const db = await initDB();
  await db.clear('pendingOrders');
  logDebug('🗑️ Cola de órdenes pendientes limpiada');
}

// ==================== METADATA DE SINCRONIZACIÓN ====================

async function setSyncMetadata(
  key: string,
  lastSync: string,
  status: 'success' | 'error'
) {
  const db = await initDB();
  await db.put('syncMetadata', { key, last_sync: lastSync, status });
}

async function _getSyncMetadata(key: string) {
  const db = await initDB();
  return await db.get('syncMetadata', key);
}

// ==================== UTILIDADES ====================

export async function getDBStats() {
  const db = await initDB();
  const productsCount = await db.count('products');
  const pendingOrdersCount = await db.count('pendingOrders');
  const categoriesCount = await db.count('categories');
  const modifiersCount = await db.count('modifiers');
  const ordersCount = await db.count('orders');
  const orderItemsCount = await db.count('orderItems');

  return {
    products: productsCount,
    pendingOrders: pendingOrdersCount,
    categories: categoriesCount,
    modifiers: modifiersCount,
    orders: ordersCount,
    orderItems: orderItemsCount,
  };
}

export async function clearAllData() {
  const db = await initDB();
  await Promise.all([
    db.clear('products'),
    db.clear('pendingOrders'),
    db.clear('categories'),
    db.clear('modifiers'),
    db.clear('orders'),
    db.clear('orderItems'),
    db.clear('syncMetadata'),
    db.clear('tables'),
  ]);
  logDebug('🗑️ Toda la base de datos local limpiada');
}

/**
 * 🚨 EMERGENCIA: Borrar completamente IndexedDB y recrearla
 * Usar solo si hay problemas graves de corrupción de datos
 */
export async function nukeDatabase() {
  try {
    logWarn('💣 Iniciando limpieza completa...');

    // 1. Cerrar conexión de IndexedDB
    if (dbInstance) {
      dbInstance.close();
      dbInstance = null;
    }

    // 2. Eliminar IndexedDB por completo
    await new Promise<void>((resolve, reject) => {
      const request = indexedDB.deleteDatabase(INDEXEDDB_NAME);
      
      request.onsuccess = () => {
        logWarn('✅ IndexedDB eliminada');
        resolve();
      };
      
      request.onerror = () => {
        devLog.error('❌ Error al eliminar IndexedDB');
        reject(request.error);
      };

      request.onblocked = () => {
        logWarn('⚠️ Eliminación bloqueada (cierra otras pestañas)');
      };
    });

    // 3. 🔥 LIMPIAR SERVICE WORKER CACHES (Workbox)
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      logWarn(`🗑️ Limpiando ${cacheNames.length} caches del Service Worker...`);
      
      await Promise.all(
        cacheNames.map(async (cacheName) => {
          await caches.delete(cacheName);
          logWarn(`🗑️ Cache "${cacheName}" eliminado`);
        })
      );
      
      logWarn('✅ Todos los caches de Workbox limpiados');
    }

    // 4. 🔥 DESREGISTRAR SERVICE WORKER (opcional, para forzar reinstalación)
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.unregister();
        logWarn('🗑️ Service Worker desregistrado');
      }
    }

    // 5. Limpiar localStorage también
    const localStorageKeys = Object.keys(localStorage);
    const cacheKeys = localStorageKeys.filter(key => 
      key.startsWith('smarty-cache-') || 
      key === 'cached_tenant_id' || 
      key === 'cached_branch_id'
    );
    cacheKeys.forEach(key => localStorage.removeItem(key));
    logWarn(`🗑️ ${cacheKeys.length} items de localStorage limpiados`);

    // 6. Recrear IndexedDB limpia
    await initDB();
    logWarn('✅ Base de datos recreada limpia');
    
    logWarn('💣 LIMPIEZA COMPLETA EXITOSA - Recarga la página (F5)');
  } catch (error) {
    devLog.error('❌ Error durante limpieza', error);
    throw error;
  }
}
